import { supabase } from '../lib/supabase';

// API response types
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: any;
}

interface SystemUserData {
  id: string;
  email: string;
  name: string;
  system_role: 'super_admin' | 'manager' | 'viewer';
  is_active: boolean;
  phone?: string;
  updated_at: string;
}

// API URL configuration using environment variable
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Vercel deployment
  if (hostname.includes('vercel.app') || hostname === 'bitgrit-os-app.vercel.app') {
    return 'https://api.os-dev.0xkaz.com';
  }
  
  // Cloudflare Pages deployment
  if (hostname.includes('pages.dev') || hostname === 'bitgrit-app.pages.dev' || hostname === 'bitgrit-admin.pages.dev') {
    return 'https://api.os-dev.0xkaz.com';
  }
  
  // Local development with virtual hosts
  if (hostname === 'frontend.bitgrit.local') {
    // Allow http for local virtual host
    return 'http://api.bitgrit.local';
  }
  
  // Local development - allow HTTP
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8766';
  }

  // For any other case (like custom domains)
  let apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_ADMIN_API_URL || 'https://api.os-dev.0xkaz.com';

  // Force HTTPS if the page is on HTTPS
  if (protocol === 'https:' && apiUrl.startsWith('http://')) {
    apiUrl = apiUrl.replace('http://', 'https://');
  }
  
  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session error:', error);
        throw new Error('セッションの取得に失敗しました');
      }
      
      if (!session || !session.access_token) {
        console.error('No valid session found');
        throw new Error('ユーザーが認証されていません');
      }
      
      console.log('Using access token:', session.access_token.substring(0, 20) + '...');
      
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      };
    } catch (error) {
      console.error('Auth headers error:', error);
      throw error;
    }
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || errorData.message || `HTTP Error: ${response.status}`);
    }

    return response.json();
  }

  // Tenant Features API (requires authentication)
  async getTenantFeatures(tenantId: string): Promise<any> {
    return this.request<any>(`/api/tenant-features/tenant/${tenantId}/features`);
  }

  async updateTenantFeatures(tenantId: string, features: any): Promise<any> {
    return this.request<any>(`/api/tenant-features/tenant/${tenantId}/features`, {
      method: 'PUT',
      body: JSON.stringify(features)
    });
  }

  async getFeaturePresets(): Promise<Record<string, any>> {
    return this.request<Record<string, any>>('/api/tenant-features/presets');
  }

  async applyFeaturePreset(tenantId: string, presetName: string): Promise<any> {
    return this.request<any>(`/api/tenant-features/tenant/${tenantId}/features/apply-preset`, {
      method: 'POST',
      body: JSON.stringify({ preset_name: presetName })
    });
  }

  // Admin API (requires system admin authentication)
  async getAllTenants(): Promise<any[]> {
    const response = await this.request<{success: boolean, data: any[]}>('/api/admin/tenants');
    return response.data || [];
  }

  async getTenantDetail(tenantId: string) {
    return this.request(`/api/admin/tenants/${tenantId}`);
  }

  async getTenantStats(tenantId: string) {
    return this.request(`/api/admin/tenants/${tenantId}/stats`);
  }

  async createTenant(data: any) {
    return this.request('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateTenant(tenantId: string, data: any) {
    return this.request(`/api/admin/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getAllUsers() {
    const response = await this.request<{success: boolean, data: any[]}>('/api/system/users/');
    return response.data || [];
  }

  async toggleUserStatus(userId: string): Promise<ApiResponse<SystemUserData>> {
    return this.request<ApiResponse<SystemUserData>>(`/api/system/users/${userId}/toggle-status`, {
      method: 'PUT'
    });
  }

  async addSystemUser(userData: { email: string; role: string; name: string; password?: string }): Promise<ApiResponse<SystemUserData>> {
    return this.request<ApiResponse<SystemUserData>>('/api/system/users/', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateSystemUser(userId: string, userData: { name?: string; system_role?: string; phone?: string; is_active?: boolean }): Promise<ApiResponse<SystemUserData>> {
    return this.request<ApiResponse<SystemUserData>>(`/api/system/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async getSystemStats() {
    return this.request('/api/admin/stats');
  }

  async getSystemHealth() {
    const response = await this.request<{success: boolean, data: any}>('/api/system/health');
    return response.data || response;
  }

  // Slack Config API
  async getSlackConfig() {
    return this.request('/api/system/slack/config');
  }

  async updateSlackConfig(config: any) {
    return this.request('/api/system/slack/config', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  async getSlackStats() {
    return this.request('/api/system/slack/stats');
  }

  async getSlackLogs(limit: number = 20) {
    return this.request(`/api/slack/logs?limit=${limit}`);
  }

  async testSlackWebhook() {
    return this.request('/api/system/slack/test', {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  // Monitoring API
  async getMonitoringErrorsSummary() {
    return this.request('/api/monitoring/errors/summary');
  }

  async getMonitoringPerformanceSummary() {
    return this.request('/api/monitoring/performance/summary');
  }

  async getMonitoringAuditSummary() {
    return this.request('/api/monitoring/audit/summary');
  }

  async getMonitoringStorageSummary() {
    return this.request('/api/monitoring/storage/summary');
  }

  async getMonitoringCleanupStatus() {
    return this.request('/api/monitoring/cleanup/status');
  }

  async getMonitoringErrors(limit: number, offset: number) {
    return this.request(`/api/monitoring/errors/?limit=${limit}&offset=${offset}`);
  }

  async getMonitoringSlowEndpoints() {
    return this.request('/api/monitoring/performance/slow-endpoints');
  }

  async getMonitoringAuditLogs(limit: number, offset: number) {
    return this.request(`/api/monitoring/audit/logs?limit=${limit}&offset=${offset}`);
  }

  async runMonitoringCleanup(type: 'daily' | 'weekly') {
    return this.request(`/api/monitoring/cleanup/run-${type}`, {
      method: 'POST'
    });
  }

  async resolveMonitoringError(errorId: string) {
    return this.request(`/api/monitoring/errors/${errorId}/resolve`, {
      method: 'POST'
    });
  }

  async markAuditLogReviewed(logId: string) {
    return this.request(`/api/monitoring/audit/${logId}/review`, {
      method: 'POST'
    });
  }

  async getMonitoringAlertConfig() {
    return this.request('/api/monitoring/alerts/config');
  }

  async updateMonitoringAlertThreshold(threshold: string, value: number) {
    return this.request(`/api/monitoring/alerts/threshold/${threshold}`, {
      method: 'PUT',
      body: JSON.stringify(value)
    });
  }

  async testMonitoringAlerts() {
    return this.request('/api/monitoring/alerts/test', {
      method: 'POST'
    });
  }


  async getTenantUsers(params: { tenant_id?: string; limit?: number; offset?: number; search?: string }) {
    const queryParams = new URLSearchParams();
    if (params.tenant_id) queryParams.append('tenant_id', params.tenant_id);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.search) queryParams.append('search', params.search);
    return this.request(`/api/admin/tenant-users?${queryParams.toString()}`);
  }

  async createTenantUser(tenantId: string, userData: {
    email: string;
    name: string;
    password: string;
    role: string;
  }) {
    return this.request(`/api/admin/tenants/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async getTenantUserDetail(userId: string) {
    return this.request(`/api/admin/tenant-users/${userId}`);
  }

  // Tenant Users API
  async inviteUserToTenant(tenantId: string, inviteData: any) {
    return this.request(`/api/admin/tenants/${tenantId}/invite`, {
      method: 'POST',
      body: JSON.stringify(inviteData)
    });
  }

  async updateTenantUser(userId: string, userData: {
    name?: string;
    role?: string;
  }) {
    return this.request(`/api/admin/tenant-users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async toggleTenantUserStatus(userId: string) {
    return this.request(`/api/admin/tenant-users/${userId}/toggle-status`, {
      method: 'POST'
    });
  }
}

export const apiService = new ApiService();
export default apiService;
export type { ApiResponse, SystemUserData };